import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@f2e/ui/components/base"
import { Check, ChevronDown } from "lucide-react"
import type { ClientUserTag } from "@/hooks/api"
import { DEFAULT_TAG_COLOR } from "./clientUserTagColors"

export interface ClientUserTagFilterProps {
  tags: ClientUserTag[]
  selectedTagIds: string[]
  open?: boolean
  onOpenChange?: (open: boolean) => void
  onChange: (tagIds: string[]) => void
}

export const ClientUserTagFilter = ({
  tags,
  selectedTagIds,
  open: controlledOpen,
  onOpenChange,
  onChange,
}: ClientUserTagFilterProps) => {
  const { t } = useTranslation()
  const [internalOpen, setInternalOpen] = useState(false)
  const keepOpenUntilRef = useRef(0)
  const reopenAfterValueChangeRef = useRef(false)
  const open = controlledOpen ?? internalOpen
  const setOpen = onOpenChange ?? setInternalOpen
  const selectedTagIdSet = useMemo(
    () => new Set(selectedTagIds),
    [selectedTagIds]
  )
  const selectedTags = useMemo(
    () => tags.filter((tag) => selectedTagIdSet.has(tag.id)),
    [selectedTagIdSet, tags]
  )

  const keepOpenAfterSelect = () => {
    reopenAfterValueChangeRef.current = true
    keepOpenUntilRef.current = Date.now() + 600
    setOpen(true)
    ;[0, 100, 250].forEach((delay) => {
      window.setTimeout(() => {
        if (Date.now() < keepOpenUntilRef.current) setOpen(true)
      }, delay)
    })
    window.setTimeout(() => {
      keepOpenUntilRef.current = 0
    }, 650)
  }

  useEffect(() => {
    if (!reopenAfterValueChangeRef.current) return
    const reopenTimer = window.setTimeout(() => {
      setOpen(true)
      reopenAfterValueChangeRef.current = false
    }, 350)
    return () => window.clearTimeout(reopenTimer)
  }, [selectedTagIds, setOpen])

  const toggleTag = (tagId: string) => {
    const next = selectedTagIdSet.has(tagId)
      ? selectedTagIds.filter((id) => id !== tagId)
      : [...selectedTagIds, tagId]
    onChange(next)
    keepOpenAfterSelect()
  }

  const label =
    selectedTags.length === 0
      ? t("crm.userManagement.filters.all")
      : selectedTags.length === 1
        ? selectedTags[0].name
        : t("crm.userManagement.filters.tagSelected", {
            count: selectedTags.length,
          })

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && Date.now() < keepOpenUntilRef.current) return
        setOpen(nextOpen)
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className="w-40 justify-between gap-2 px-3 font-normal"
        >
          <span className="min-w-0 truncate">{label}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={6}
        collisionPadding={8}
        className="w-72 overflow-hidden p-0"
      >
        {tags.length > 0 ? (
          <Command>
            <CommandInput placeholder={t("crm.userManagement.tags.search")} />
            {selectedTags.length > 1 && (
              <div className="border-b px-3 py-2 text-xs text-muted-foreground">
                {t("crm.userManagement.filters.tagMatchAllHelp")}
              </div>
            )}
            <CommandList className="max-h-60">
              <CommandEmpty>
                {t("crm.userManagement.tags.noSearchResults")}
              </CommandEmpty>
              <CommandGroup>
                <CommandItem
                  onMouseDown={(event) => {
                    event.preventDefault()
                    event.stopPropagation()
                  }}
                  onSelect={() => {
                    onChange([])
                    keepOpenAfterSelect()
                  }}
                >
                  <span className="h-2 w-2 shrink-0" />
                  <span className="min-w-0 flex-1 truncate">
                    {t("crm.userManagement.filters.allTags")}
                  </span>
                  {selectedTags.length === 0 && <Check className="h-4 w-4" />}
                </CommandItem>
                {tags.map((tag) => {
                  const selected = selectedTagIdSet.has(tag.id)
                  return (
                    <CommandItem
                      key={tag.id}
                      value={`${tag.name} ${tag.id}`}
                      onMouseDown={(event) => {
                        event.preventDefault()
                        event.stopPropagation()
                      }}
                      onSelect={() => toggleTag(tag.id)}
                    >
                      <span
                        className="h-2 w-2 shrink-0 rounded-full border"
                        style={{
                          backgroundColor: tag.color || DEFAULT_TAG_COLOR,
                          borderColor: tag.color || DEFAULT_TAG_COLOR,
                        }}
                      />
                      <span className="min-w-0 flex-1 truncate">
                        {tag.name}
                      </span>
                      {selected && <Check className="h-4 w-4" />}
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        ) : (
          <div className="p-4 text-sm text-muted-foreground">
            {t("crm.userManagement.tags.empty")}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
